import React from 'react';
import { map, head, get } from 'lodash';
import SimpleBar from 'simplebar-react';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { textareaField as TextareaField } from 'ui/views/generic/textareaField';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { EndScrollDetector } from 'modules/end-scroll-detector';


/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { toDoItemDetailActivityWidget as ToDoItemDetailActivityWidget } from 'ui/views/toDoItemDetailActivityWidget';
 *
 * // render
 * <ToDoItemDetailActivityWidget propName='propValue'/>
 */
const _toDoItemDetailActivityWidget = ( props ) => {

    const activityTitle = get( props, 'structure.widgetTitle.title', {} );
    const additionalCommentDetails = head( props.structure.additionalComment.field );
    const additionalCommentAction = head( props.structure.additionalComment.actions );
    const notesDetails = head( props.structure.notes.field );
    const notesAction = head( props.structure.notes.actions );
    
    return (
        <div className='ui-to-do-item-detail-activity-widget'>
            <div className='ui-to-do-item-detail-activity-widget__activities'>
                <div className='ui-to-do-item-detail-activity-widget__activities__title'>
                    { getLabel( activityTitle ) }
                </div>
                <EndScrollDetector
                    disabled={ props.disablePagination }
                    callback={ props.onPagination }
                    render={ ( { ref } ) => (
                        <SimpleBar scrollableNodeProps={ { ref } } style={ { maxHeight: 380 } }>
                            <div className='ui-to-do-item-detail-activity-widget__activities__items'>
                                {
                                    map( props.data.state.items, ( item ) => {
                                        return (
                                            <div key={item.id} className='ui-to-do-item-detail-activity-widget__activities__items__item'>
                                                <div className='ui-to-do-item-detail-activity-widget__activities__items__item__initialsbox'>
                                                    <div className='ui-to-do-item-detail-activity-widget__activities__items__item__initialsbox__initials'>
                                                        {/* get initials using regex */}
                                                        { item.username.match( /(\b\S)?/g ).join( '' ).toUpperCase() }
                                                    </div>
                                                </div>
                                                <div className='ui-to-do-item-detail-activity-widget__activities__items__item__info'>
                                                    <div className='ui-to-do-item-detail-activity-widget__activities__items__item__info__name'>
                                                        { item.username } <span className='ui-to-do-item-detail-activity-widget__activities__items__item__info__name--faded'>({ item.type })</span>
                                                    </div>
                                                    <div className='ui-to-do-item-detail-activity-widget__activities__items__item__info__date'>
                                                        { item.date }
                                                    </div>
                                                    <div className='ui-to-do-item-detail-activity-widget__activities__items__item__info__description'>
                                                        { item.note }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    } )
                                }
                            </div>
                        </SimpleBar>
                    ) }
                />
            </div>
            <div className='ui-to-do-item-detail-activity-widget__additional'>
                <div className='ui-to-do-item-detail-activity-widget__additional__comment'>
                    <TextareaField
                        label={ get( additionalCommentDetails, 'title', {} ) }
                        placeholder={ get( additionalCommentDetails, 'placeholder', {} )}
                        name={ get( additionalCommentDetails, 'name', '' ) }
                        value={ props.additionalCommentValue }
                        onChange={ props.onChange }
                        textAreaClassname='ui-to-do-item-detail-activity-widget__additional__comment__textarea'
                        labelClassname='ui-to-do-item-detail-activity-widget__additional__comment__label'
                    />
                    <Button
                        id={ get( additionalCommentDetails, 'name', '' ) }
                        type='button'
                        size='medium'
                        { ...additionalCommentAction }
                        disabled={ props.additionalCommentActionDisabled }
                        outline={ false }
                        onClick={ () => props.onPost( {
                            name: additionalCommentDetails.name,
                            url: additionalCommentAction.url
                        } ) }
                        className='ui-to-do-item-detail-activity-widget__additional__comment__button'
                    />
                </div>
                <div className='ui-to-do-item-detail-activity-widget__additional__notes'>
                    <TextareaField
                        label={ get( notesDetails, 'title', {} ) }
                        placeholder={ get( notesDetails, 'placeholder', {} )}
                        name={ get( notesDetails, 'name', '' ) }
                        value={ props.notesValue }
                        onChange={ props.onChange }
                        textAreaClassname='ui-to-do-item-detail-activity-widget__additional__notes__textarea'
                        labelClassname='ui-to-do-item-detail-activity-widget__additional__notes__label'
                    />
                    <Button
                        id={ notesAction.name }
                        type='button'
                        size='medium'
                        { ...notesAction }
                        disabled={ props.notesActionDisabled }
                        outline={ false }
                        onClick={ () => props.onPost( {
                            name: notesDetails.name,
                            url: notesAction.url
                        } ) }
                        className='ui-to-do-item-detail-activity-widget__additional__notes__button'
                    />
                </div>
            </div>
        </div>
    );
};

// set display name
_toDoItemDetailActivityWidget.displayName = 'toDoItemDetailActivityWidget';

// set default props
_toDoItemDetailActivityWidget.defaultProps = {};

// prop types
_toDoItemDetailActivityWidget.propTypes = {};

// preloader view
const toDoItemDetailActivityWidgetPreloader= () => {
    return (
        <div className='ui-to-do-item-detail-activity-widget-preloader'>
            <div className='ui-to-do-item-detail-activity-widget-preloader__left'>
                <div className='ui-to-do-item-detail-activity-widget-preloader__left__header'>
                    <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__title'>
                        <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__title__text'></div>
                    </div>
                    { map( [ 1, 2, 3, 4 ], item => {
                        return (
                            <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__box' key={ item } >
                                <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__box__title'></div>
                                <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__box__details'>
                                    <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__box__details__title'></div>
                                    <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__box__details__subtitle'></div>
                                    <div className='ui-to-do-item-detail-activity-widget-preloader__left__header__box__details__desc'></div>
                                </div>

                            </div>
                        );
                    } ) }
                </div>
            </div>
            <div className='ui-to-do-item-detail-activity-widget-preloader__right'>
                { map( [ 1, 2 ], item => {
                    return (
                        <div className='ui-to-do-item-detail-activity-widget-preloader__right__box' key={ item } >
                            <div className='ui-to-do-item-detail-activity-widget-preloader__right__box__title'>
                                <div className='ui-to-do-item-detail-activity-widget-preloader__right__box__title__text'></div>
                            </div>
                            <div className='ui-to-do-item-detail-activity-widget-preloader__right__box__section'></div>
                        </div>
                    );
                } ) }
            </div>
           
        </div>
    );
};

// set display name
toDoItemDetailActivityWidgetPreloader.displayName = 'toDoItemDetailActivityWidgetPreloader';

// set default props
toDoItemDetailActivityWidgetPreloader.defaultProps = {};

// prop types
toDoItemDetailActivityWidgetPreloader.propTypes = {};

export const toDoItemDetailActivityWidget = withNetworkState( toDoItemDetailActivityWidgetPreloader, _toDoItemDetailActivityWidget );

